import React, { useCallback } from 'react'
import { Badge, Button, Col, Grid, Icon, Typography } from 'gu-ui-kit'
import clsx from 'clsx'
import formatMoney from 'utils/formatMoney'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import PropTypes from 'prop-types'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { useWallets } from 'modules/Finance/Wallets'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styles from 'modules/Finance/Wallets/subcomponents/WalletsList/WalletsList.module.scss'
import { useSelector } from 'react-redux'

export const List = ({ popover = false }) => {
  const breakpoint = useBreakpoint()
  const [conf, setConf] = useLocalStorage('wallets-list', {})
  const { walletID } = useParams()
  const { t } = useTranslation()
  const { setActiveWalletID } = useWallets()

  const { wallets: { data: wallets = [] } = {} } = useSelector((state) => state['finance/wallets'])

  const handlePin = useCallback(
    ({ event, id }) => {
      event.stopPropagation()
      const orders = Object.values(conf).map(({ order }) => order)
      const minOrder = orders.length ? Math.min(...orders) : 0
      const { pinned = false } = conf?.[id] ?? {}
      setConf({ [id]: { pinned: !pinned, order: !pinned ? minOrder - 1 : '' } })
    },
    [conf],
  )

  return (
    <Grid
      className={clsx(styles['wallets-list__list'], {
        [styles['wallets-list__list_popover']]: popover,
      })}
      gap={popover ? 0 : 8}
      direction={breakpoint.lg || popover ? 'column' : 'row'}
      wrap={false}
    >
      {wallets.map((wallet) => {
        const {
          id,
          currency,
          balance,
          name = null,
          financial_profile: { financial_profile_type: { type = null }, custom_name } = {},
        } = wallet
        const { pinned = false, order = '' } = conf?.[id] ?? {}
        const isCurrentWalletActive = +walletID === +id
        return (
          <Grid
            gap={8}
            direction={'column'}
            key={id}
            title={`${name || custom_name || t('finance::types.' + type)}: ${balance} ${currency}`}
            className={clsx(styles['wallets-list__item'], {
              [styles['wallets-list__item_active']]: isCurrentWalletActive,
              [styles['wallets-list__item_popover']]: popover,
            })}
            onClick={() => setActiveWalletID(id)}
            style={{ order: popover ? '' : order }}
          >
            <Grid
              direction={popover ? 'row' : 'column'}
              gap={4}
              wrap={false}
              justifyContent={popover ? 'between' : 'start'}
            >
              <Grid
                direction={'column'}
                gap={4}
                className={styles['wallets-list__item-column']}
              >
                <Grid
                  alignItems={'center'}
                  justifyContent={'between'}
                  wrap={false}
                >
                  <Col className={styles['wallets-list__heading']}>
                    <Typography
                      noWrap
                      variant={popover ? 'Body-15' : 'Subtitle-20'}
                      color={isCurrentWalletActive ? 'Label-Fifth' : 'Label-Secondary'}
                    >
                      {name || custom_name || t('wallets::nav.my_account')}
                    </Typography>
                  </Col>
                  {!popover && (
                    <Button
                      className={clsx(styles['wallets-list__pin'], {
                        [styles['wallets-list__pin_pinned']]: pinned,
                      })}
                      variant={'ghost'}
                      link
                      onClick={(event) => handlePin({ event, id })}
                    >
                      <Icon
                        width={20}
                        //color={pinned ? 'Interface-GU-Primary' : 'Interface-Seventh'}
                        color={'inherit'}
                        name={'pin'}
                      />
                    </Button>
                  )}
                </Grid>
                <Grid
                  direction={'row'}
                  gap={8}
                  alignItems={'center'}
                  justifyContent={'start'}
                  wrap={false}
                >
                  <Badge
                    size={'xs'}
                    variant={'Body-10'}
                    bgColor={popover ? 'Background-Six' : 'Background-Fifth'}
                    color={'Label-GU-Primary'}
                    noWrap
                  >
                    {currency.toUpperCase()}
                  </Badge>
                  <Badge
                    size={'xs'}
                    bgColor={popover ? 'Background-Six' : 'Background-Fifth'}
                    variant={'Body-10'}
                    color={'Label-Fifth'}
                    noWrap
                  >
                    {t(`finance::types.${type}`)}
                  </Badge>
                  {custom_name && (
                    <Typography
                      variant={'Body-10'}
                      color={'Label-Secondary'}
                      noWrap
                      component={'div'}
                      className={styles['wallets-list__custom-name']}
                    >
                      {custom_name}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Typography
                variant={'Body-20'}
                color={'Label-Fifth'}
                component={'div'}
                noWrap
                dangerouslySetInnerHTML={{ __html: formatMoney(balance, currency) }}
              />
            </Grid>
          </Grid>
        )
      })}
    </Grid>
  )
}

List.propTypes = {
  popover: PropTypes.bool,
}
